import type { Cube, Query } from '@cubejs-client/core'

import { viewFields } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { periodLabel, periodRange, previousPeriod } from '@/lib/period'
import {
  documentRunCreateSchema,
  type DocNode,
  type DocumentRunCreate,
  type ReportDocument,
} from '@/lib/report-document'
import type { DocumentRun } from '@/services/bi/runs'
import {
  chartWidgetQuery,
  groupedTableQuery,
  kpiQuery,
  listPageQuery,
  totalsQuery,
} from '@/services/cube/dashboard'
import {
  documentFilterDefs,
  missingMembers,
  namedQuery,
  pageFilterDefs,
  withPeriod,
} from '@/services/cube/document'
import type { ReportFilterState, ReportRow } from '@/services/cube/report'

import type { WidgetNodeName } from '../nodes/data-source'
import { buildDocumentWidget } from '../nodes/widget-model'
import { namedDataKey, previousDataKey, totalsDataKey, widgetDataKey } from './data-keys'
import { selectedText } from './filter-text'
import { diffRefresh, materialize, type QueryResults, type RefreshDiff } from './materialize'

/*
 * Dönem kopyası üretimi (docs/REPORT-DOCUMENTS-PLAN.md §4.2 adım 1–4): tüm sorgular (adlandırılmış +
 * her widget'ınki) tarayıcıda çalışır, sonuçlar `data` anahtarlarıyla (`data-keys.ts`) toplanır,
 * içerik `materialize` ile açılır. Backend yalnız doğrular ve saklar (kilitli karar).
 */

/** Üretimde seçilen filtreler: üye kısa adı -> değer (`document_runs.filters`). */
export type RunFilterValues = DocumentRunCreate['filters']

/** `forbidden`: view token izninde yok; `invalid`: şablondaki sorgu/widget tanımı geçersiz. */
export class RunGenerationError extends Error {
  readonly reason: 'forbidden' | 'invalid'

  constructor(reason: 'forbidden' | 'invalid', message: string) {
    super(message)
    this.name = 'RunGenerationError'
    this.reason = reason
  }
}

const WIDGET_TYPES: ReadonlySet<string> = new Set<WidgetNodeName>([
  'reportKpi',
  'reportTable',
  'reportList',
  'reportChart',
])
const DEFAULT_LIST_LIMIT = 50

export type Loader = (query: Query) => Promise<ReportRow[]>

/** Şablonda geçen widget düğümleri (koşulun iki dalı ve tekrar içi dahil), özniteliğe göre tekilleştirilmiş. */
function collectWidgets(nodes: DocNode[], found = new Map<string, DocNode>()) {
  for (const node of nodes) {
    if (WIDGET_TYPES.has(node.type)) {
      found.set(widgetDataKey(node.type as WidgetNodeName, node.attrs ?? {}), node)
    }
    collectWidgets(node.content ?? [], found)
  }
  return found
}

function filterState(
  definition: ReportDocument,
  period: string,
  filters: RunFilterValues,
): ReportFilterState {
  const [dateFrom, dateTo] = periodRange(period)
  const state: ReportFilterState = {
    dateFrom,
    dateTo,
    multi: {},
    bool: {},
    columns: {},
    selections: [],
  }
  for (const filter of definition.filters) {
    const value = filters[filter.member]
    const key = `${filter.view}.${filter.member}`
    if (filter.type === 'multiSelect' && Array.isArray(value)) state.multi[key] = value
    if (filter.type === 'boolean' && typeof value === 'boolean') state.bool[key] = value
  }
  return state
}

interface RunQueryArgs {
  definition: ReportDocument
  views: ReadonlyMap<string, Cube>
  period: string
  filters: RunFilterValues
  load: Loader
}

/** Tüm sorguları (adlandırılmış + widget) çalıştırır; `data` anahtarlarıyla toplar. */
async function fetchRunData(args: RunQueryArgs): Promise<DocumentRunCreate['data']> {
  const { definition, views, period, filters, load } = args
  const state = filterState(definition, period, filters)
  const pageFilters = pageFilterDefs(documentFilterDefs(definition.filters))
  const previousState = withPeriod(state, previousPeriod(period))
  const data: DocumentRunCreate['data'] = {}
  const jobs: Promise<void>[] = []
  const fetchInto = (key: string, query: Query) =>
    jobs.push(load(query).then((rows) => void (data[key] = rows)))

  for (const [name, def] of Object.entries(definition.queries)) {
    const cube = views.get(def.view)
    if (!cube)
      throw new RunGenerationError(
        'forbidden',
        messages.documents.runs.generate.forbidden(def.view),
      )
    const fields = viewFields(cube).fields
    const missing = missingMembers(def, fields)
    if (missing.length > 0) {
      throw new RunGenerationError(
        'invalid',
        messages.documents.editor.memberMissing(`${def.view}.${missing[0]}`),
      )
    }
    const key = namedDataKey(name)
    fetchInto(key, namedQuery(def, fields, pageFilters, state))
    if (def.compare === 'previous') {
      fetchInto(previousDataKey(key), namedQuery(def, fields, pageFilters, previousState))
    }
  }

  for (const [key, node] of collectWidgets(definition.content.content)) {
    const built = buildDocumentWidget(
      node.type as WidgetNodeName,
      node.attrs ?? {},
      views,
      pageFilters,
      period,
    )
    if (!built.ok) {
      const view = String(node.attrs?.view ?? '')
      throw built.reason === 'forbidden'
        ? new RunGenerationError('forbidden', messages.documents.runs.generate.forbidden(view))
        : new RunGenerationError('invalid', built.message)
    }
    const { widget } = built
    switch (widget.type) {
      case 'kpi':
        fetchInto(key, kpiQuery(widget, state))
        fetchInto(previousDataKey(key), kpiQuery(widget, previousState))
        break
      case 'table':
        fetchInto(key, groupedTableQuery(widget, state))
        if (widget.totals)
          fetchInto(totalsDataKey(key), totalsQuery(widget, widget.measures, state))
        break
      case 'list':
        fetchInto(key, listPageQuery(widget, state, [], 1, built.limit ?? DEFAULT_LIST_LIMIT))
        if (widget.totals.length > 0) {
          fetchInto(totalsDataKey(key), totalsQuery(widget, widget.totals, state))
        }
        break
      case 'chart':
        fetchInto(key, chartWidgetQuery(widget, state))
        break
    }
  }

  await Promise.all(jobs)
  return data
}

const rowsOf = (data: Record<string, unknown>, key: string) =>
  Array.isArray(data[key]) ? (data[key] as ReportRow[]) : undefined

/** `data` anahtarlarından adlandırılmış sorgu sonuçlarını kurar (yetkisiz view atlanır). */
function resultsFromData(
  definition: ReportDocument,
  views: ReadonlyMap<string, Cube>,
  data: Record<string, unknown>,
): QueryResults {
  const results: QueryResults = {}
  for (const [name, def] of Object.entries(definition.queries)) {
    const cube = views.get(def.view)
    if (!cube) continue
    const key = namedDataKey(name)
    results[name] = {
      view: def.view,
      fields: viewFields(cube).fields,
      rows: rowsOf(data, key),
      previousRows: rowsOf(data, previousDataKey(key)),
      state: 'ready',
      status: {},
    }
  }
  return results
}

function materializeContext(definition: ReportDocument, period: string, filters: RunFilterValues) {
  return {
    periodLabel: periodLabel(period),
    filterText: (member: string) =>
      selectedText({ filters: definition.filters, filterValues: filters }, member),
  }
}

/**
 * Şablon + dönem + filtrelerden kopya gövdesini üretir. Yetkisiz view ya da geçersiz tanım
 * `RunGenerationError` fırlatır; hiçbir şey kaydedilmez.
 */
export async function buildRun(args: RunQueryArgs): Promise<DocumentRunCreate> {
  const { definition, views, period, filters } = args
  const data = await fetchRunData(args)
  const { content, conditionResults } = materialize(
    definition,
    resultsFromData(definition, views, data),
    materializeContext(definition, period, filters),
  )
  return { period, filters, content, data, condition_results: conditionResults }
}

export interface RunRefresh {
  /** `POST .../refresh` gövdesi (`expected_version` hariç); metin (`content`) gönderilmez. */
  body: Pick<DocumentRunCreate, 'data' | 'condition_results'>
  diff: RefreshDiff
}

/**
 * "Verileri yenile": kopyanın dönem ve filtreleriyle sorgular yeniden çalışır; yeni `data` ve
 * `condition_results` ile değişenler (`diffRefresh`) döner. Kopya metni değişmez.
 */
export async function buildRefresh(args: {
  definition: ReportDocument
  views: ReadonlyMap<string, Cube>
  run: Pick<DocumentRun, 'period' | 'filters' | 'content' | 'data' | 'condition_results'>
  load: Loader
}): Promise<RunRefresh> {
  const { definition, views, run, load } = args
  const shape = documentRunCreateSchema.shape
  const content = shape.content.safeParse(run.content)
  const filters = shape.filters.safeParse(run.filters)
  const previousConditionResults = shape.condition_results.safeParse(run.condition_results)
  if (!content.success || !filters.success || !previousConditionResults.success) {
    throw new RunGenerationError('invalid', messages.documents.runs.refresh.invalidRun)
  }

  const data = await fetchRunData({
    definition,
    views,
    period: run.period,
    filters: filters.data,
    load,
  })
  const next = resultsFromData(definition, views, data)
  const source = materializeContext(definition, run.period, filters.data)
  const diff = diffRefresh({
    definition,
    content: content.data,
    previousConditionResults: previousConditionResults.data,
    previous: resultsFromData(definition, views, run.data),
    next,
    source,
  })
  const { conditionResults } = materialize(definition, next, source)
  return { body: { data, condition_results: conditionResults }, diff }
}
